import { useContext, useEffect, useRef, useState } from "react";
import LoginImage from "../../assets/img/skor_login_img.png";
import Logo from "../../assets/img/logo.svg";
import useTranslator from "../../hooks/useTranslator";
import i18next from "i18next";
import AuthContext from "../../context/auth/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import "./onboardingProgress.css";

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="m4.5 10.2 3.3 3.3 7.7-7.7" />
  </svg>
);

const OnboardingProgress = ({
  step,
  identityStage,
  skipFinancialStudy,
  companyLogo,
  identityStillValid = false,
  isCompany = false,
  isIframe = false,
  hideLanguageSwitcher = false,
}) => {
  const { _t } = useTranslator();
  const { changeUserLng, user } = useContext(AuthContext);
  const [currentLang, setCurrentLang] = useState(
    () => localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
  );
  const [isLangChanging, setIsLangChanging] = useState(false);
  const isChangingLang = useRef(false);

  useEffect(() => {
    if (user?.lang && user.lang.toLowerCase() !== currentLang) {
      // sync if backend lang differs and not currently changing
      if (!isChangingLang.current) {
        const normalized = user.lang.toLowerCase();
        setCurrentLang(normalized);
        i18next.changeLanguage(normalized);
      }
    }
  }, [user?.lang, currentLang]);

  const handleLanguageChange = async (langVal) => {
    if (isChangingLang.current || isLangChanging) return;
    isChangingLang.current = true;
    setIsLangChanging(true);
    try {
      await i18next.changeLanguage(langVal);
      try {
        await changeUserLng?.({ skipUserLoad: true });
      } catch {}
      setCurrentLang(langVal.toLowerCase());
    } finally {
      setTimeout(() => {
        isChangingLang.current = false;
        setIsLangChanging(false);
      }, 1500);
    }
  };

  useEffect(() => {
    document.body.classList.add("tenant-onboarding-progress-visible");
    return () =>
      document.body.classList.remove("tenant-onboarding-progress-visible");
  }, []);

  const steps = [
    {
      id: "details",
      title: _t("yourDetails", "Your details"),
    },
    {
      id: "identity",
      title: _t("identity", "Identity"),
      activeSubstep: identityStage,
      substeps: [
        { id: "document", title: _t("document", "Document") },
        { id: "photos", title: _t("photos", "Photos") },
        { id: "processing", title: _t("processing", "Processing") },
      ],
      ...(identityStillValid ? { badge: _t("stillValid", "Still valid") } : {}),
    },
    {
      id: "documents",
      title: _t("documents", "Documents"),
      activeSubstep:
        step === 3 ? "upload" : step === 4 ? "review" : "processing",
      substeps: [
        { id: "upload", title: _t("upload", "Upload") },
        { id: "review", title: _t("review", "Review") },
        { id: "processing", title: _t("processing", "Processing") },
      ],
    },
    ...(!skipFinancialStudy && !isCompany
      ? [
          {
            id: "financial",
            title: _t("financialStudy", "Financial study"),
            badge: _t("recommended", "Recommended"),
          },
        ]
      : []),
    ...(!isCompany && !isIframe
      ? [
          {
            id: "application",
            title: _t("application", "Application"),
          },
        ]
      : [
          {
            id: "completed",
            title: _t("profileCompleted", "Profile completed"),
          },
        ]),
  ];

  const showCompletedStep = isCompany || isIframe;
  const requestedActiveId =
    step === 1
      ? "details"
      : step === 2
        ? "identity"
        : step <= 5
          ? "documents"
          : step === 6 && !skipFinancialStudy && !isCompany
            ? "financial"
            : showCompletedStep
              ? "completed"
              : "application";
  // When the financial step is hidden, fall back to the completion step.
  const fallbackId =
    requestedActiveId === "financial" && showCompletedStep
      ? "completed"
      : requestedActiveId === "application" && showCompletedStep
        ? "completed"
        : requestedActiveId;
  const activeIndexRaw = steps.findIndex(({ id }) => id === fallbackId);
  const activeIndex = activeIndexRaw === -1 ? 0 : activeIndexRaw;
  const activeStep = steps[activeIndex];

  return (
    <aside
      className="onboarding-progress"
      aria-label={_t("yourProcess", "Your process")}
    >
      <img className="onboarding-progress__image" src={LoginImage} alt="" />
      <div className="onboarding-progress__shade" aria-hidden="true" />

      {/* Language switcher at top right – visible only on mobile, inside top nav.
          Hidden when parent already renders one (e.g. renewal topbar) to avoid duplicates. */}
      {!hideLanguageSwitcher && (
        <div
          className="onboarding-progress__language"
          aria-label="language switch"
        >
          <LanguageSwitcher
            currentLang={currentLang}
            isLangChanging={isLangChanging}
            onChange={handleLanguageChange}
          />
        </div>
      )}

      <div className="onboarding-progress__content">
        <img className="onboarding-progress__logo" src={Logo} alt="SKOR" />
        <p className="onboarding-progress__eyebrow">
          {_t("yourProcess", "Your process")}
        </p>

        <ol className="onboarding-progress__steps">
          {steps.map((progressStep, index) => {
            const isActive = index === activeIndex;
            const isComplete = index < activeIndex;
            const activeSubstepIndex = progressStep.substeps?.findIndex(
              ({ id }) => id === progressStep.activeSubstep
            );

            return (
              <li
                className={`onboarding-progress__step ${
                  isActive ? "is-active" : isComplete ? "is-complete" : ""
                }`}
                key={progressStep.id}
                aria-current={isActive ? "step" : undefined}
              >
                <span className="onboarding-progress__marker">
                  {isComplete ? <CheckIcon /> : index + 1}
                </span>
                <div className="onboarding-progress__step-content">
                  <div className="onboarding-progress__title-row">
                    <span className="onboarding-progress__title text-capitalize">
                      {progressStep.title}
                    </span>
                    {progressStep.badge && (
                      <span
                        className={`onboarding-progress__badge${progressStep.id === "identity" && identityStillValid ? " onboarding-progress__badge--valid" : ""}`}
                      >
                        {progressStep.badge}
                      </span>
                    )}
                  </div>

                  {isComplete && (
                    <span className="onboarding-progress__completed">
                      {_t("completed", "Completed")}
                    </span>
                  )}

                  {isActive && progressStep.substeps && (
                    <ol className="onboarding-progress__substeps">
                      {progressStep.substeps.map((substep, substepIndex) => {
                        const isSubstepActive =
                          substep.id === progressStep.activeSubstep;
                        const isSubstepComplete =
                          activeSubstepIndex > substepIndex;

                        return (
                          <li
                            className={`onboarding-progress__substep ${
                              isSubstepActive
                                ? "is-active"
                                : isSubstepComplete
                                  ? "is-complete"
                                  : ""
                            }`}
                            key={substep.id}
                            aria-current={isSubstepActive ? "step" : undefined}
                          >
                            <span className="onboarding-progress__submarker">
                              {isSubstepComplete && <CheckIcon />}
                            </span>
                            <span>{substep.title}</span>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              </li>
            );
          })}
        </ol>

        {companyLogo && (
          <div className="onboarding-progress__company">
            <div
              className="onboarding-progress__company-mask"
              aria-hidden="true"
            />
            <img
              className="onboarding-progress__company-logo"
              src={companyLogo}
              alt="Company logo"
            />
          </div>
        )}

        <div className="onboarding-progress__mobile">
          <img
            className="onboarding-progress__mobile-logo"
            src={Logo}
            alt="SKOR"
          />
          <div className="onboarding-progress__mobile-process">
            <div
              className="onboarding-progress__mobile-track"
              style={{
                left: `${50 / steps.length}%`,
                right: `${50 / steps.length}%`,
              }}
              aria-hidden="true"
            >
              <span
                style={{
                  width: `${
                    steps.length > 1
                      ? (activeIndex / (steps.length - 1)) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
            <ol
              className="onboarding-progress__mobile-steps"
              style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
            >
              {steps.map((progressStep, index) => {
                const isActive = index === activeIndex;
                const isComplete = index < activeIndex;

                return (
                  <li
                    className={`${isActive ? "is-active" : ""} ${
                      isComplete ? "is-complete" : ""
                    }`}
                    key={progressStep.id}
                    aria-label={progressStep.title}
                    aria-current={isActive ? "step" : undefined}
                  >
                    <span>
                      {isComplete ? <CheckIcon /> : <span aria-hidden="true" />}
                    </span>
                  </li>
                );
              })}
            </ol>
            <div
              className="onboarding-progress__mobile-label"
              style={{ gridTemplateColumns: `repeat(${steps.length}, 1fr)` }}
              aria-live="polite"
            >
              <strong style={{ gridColumn: activeIndex + 1 }}>
                {activeStep.title}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default OnboardingProgress;
